import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Bell, MessageSquare, User, Edit2, LogOut, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Shield, Coins, Zap, Activity, Target, ChevronLeft, ChevronRight, TrendingUp, Flame } from 'lucide-react';
import VerifiedBadge from '../common/VerifiedBadge';
import BurnForBoostModal from '../boosts/BurnForBoostModal';

const DesktopSidebar = ({ 
  sidebarCollapsed, 
  setSidebarCollapsed, 
  darkMode, 
  setDarkMode, 
  activeTab, 
  handleTabChange, 
  unreadNotifications, 
  unreadDMs, 
  currentUser, 
  handleUserSelection, 
  handleLogout,
  following,
  toggleFollow,
  suggestedUser,
  suggestedUserStats,
  hasPendingRankUp = false,
  onRankUpClick,
  setShowTradingPost,
  setPostType
}) => {
  const [showBurnModal, setShowBurnModal] = useState(false);
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Check if buttons would require scrolling
  useEffect(() => {
    const checkScreenSize = () => {
      // Check if sidebar content would overflow
      const viewportHeight = window.innerHeight;
      // More aggressive thresholds - activate carousel earlier to prevent scrolling
      const estimatedContentHeight = sidebarCollapsed ? 800 : 1000; 
      // Enable carousel on screens under 1000px height when expanded
      setIsSmallScreen(viewportHeight < estimatedContentHeight);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [sidebarCollapsed]);

  // Button configurations
  const actionButtons = [
    {
      id: 'post',
      label: 'Post',
      icon: Edit2,
      gradient: 'from-pink-500 to-purple-600',
      shadowColor: 'shadow-pink-500/40',
      onClick: () => {
        if (activeTab !== 'home') {
          handleTabChange('home');
        }
        setTimeout(() => {
          if (setPostType) {
            setPostType('regular');
          }
          setTimeout(() => {
            const postBox = document.querySelector('textarea[placeholder*="Post whatever"]');
            if (postBox) {
              postBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => postBox.focus(), 100);
            }
          }, 200);
        }, 150);
      }
    },
    {
      id: 'call',
      label: 'Make a Prediction',
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-green-600',
      shadowColor: 'shadow-emerald-500/50',
      onClick: () => {
        if (activeTab !== 'home') {
          handleTabChange('home');
        }
        setTimeout(() => {
          if (setPostType) {
            setPostType('automated');
          }
        }, 100);
      }
    },
    {
      id: 'burn',
      label: 'Burn TopBlast',
      icon: Coins,
      gradient: 'from-orange-500 to-red-500',
      shadowColor: 'shadow-orange-500/40',
      onClick: () => setShowBurnModal(true)
    }
  ];

  const nextButton = () => {
    setActiveButtonIndex((prev) => (prev + 1) % actionButtons.length);
  };

  const prevButton = () => {
    setActiveButtonIndex((prev) => (prev - 1 + actionButtons.length) % actionButtons.length);
  };

  return (
    <div className={`hidden md:flex h-screen sticky top-0 flex-col transition-all duration-500 ease-in-out z-50 ${
      sidebarCollapsed ? 'w-20' : 'w-64 3xl:w-72 4xl:w-80'
    }`}>
      <div className="flex flex-col h-full relative">
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin p-4 pb-0">
          <div className="mb-4">
            {/* Logo and Collapse Button Container */}
            <div className="mb-4">
            <img 
              src="/newlogo.gif" 
              alt="TopBlast" 
              className={`cursor-pointer transition-all duration-500 ease-in-out mx-auto ${sidebarCollapsed ? 'h-20 w-auto' : 'h-32 w-auto'}`}
              onClick={() => handleTabChange('home')}
            />
          </div>
          
        </div>
        
        <nav className="space-y-2">
          <button
            onClick={() => handleTabChange('home')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'home' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Home' : ''}
          >
            <Home className="w-6 h-6 text-white" />
            {!sidebarCollapsed && <span>Home</span>}
          </button>
          <button
            onClick={() => handleTabChange('big-movers')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'big-movers' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Big Movers' : ''}
          >
            <Flame className="w-6 h-6 text-red-500" />
            {!sidebarCollapsed && (
              <span className={activeTab === 'big-movers' ? 'text-red-400' : ''}>
                Big Movers
              </span>
            )}
          </button>
          <button 
            onClick={() => handleTabChange('xp-dashboard')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'xp-dashboard' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Analytics' : ''}
          >
            <Activity className="w-6 h-6 text-yellow-500" />
            {!sidebarCollapsed && (
              <span className={activeTab === 'xp-dashboard' ? 'text-yellow-400' : ''}>
                Analytics
              </span>
            )}
          </button>
          <button 
            onClick={() => {
              if (hasPendingRankUp && onRankUpClick) {
                onRankUpClick();
              }
              handleTabChange('level-up');
            }}
            className={`relative flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'level-up' ? 'font-bold text-purple-400' : ''
            } ${
              hasPendingRankUp ? 'level-up-pending level-up-pulse' : ''
            }`}
            title={sidebarCollapsed ? (hasPendingRankUp ? 'Rank Up Available!' : 'Rank Up') : ''}
          >
            <div className={`relative ${hasPendingRankUp ? 'level-up-bounce' : ''}`}>
              <Zap className={`w-6 h-6 ${hasPendingRankUp ? 'level-up-glow text-purple-400' : 'text-purple-500'}`} />
              {hasPendingRankUp && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-pink-500 rounded-full animate-ping"></div>
              )}
            </div>
            {!sidebarCollapsed && (
              <span className={hasPendingRankUp ? 'level-up-glow font-bold' : ''}>
                Rank Up{hasPendingRankUp ? ' 🎉' : ''}
              </span>
            )}
            {hasPendingRankUp && !sidebarCollapsed && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </button>
          <button 
            onClick={() => handleTabChange('explore')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'explore' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Explore' : ''}
          >
            <Search className="w-6 h-6 text-blue-500" />
            {!sidebarCollapsed && <span className={activeTab === 'explore' ? 'text-blue-400' : ''}>Explore</span>}
          </button>
          <button 
            onClick={() => handleTabChange('notifications')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left relative ${
              activeTab === 'notifications' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Notifications' : ''}
          >
            <div className="relative">
              <Bell className="w-6 h-6 text-orange-500" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </div>
            {!sidebarCollapsed && <span className={activeTab === 'notifications' ? 'text-orange-400' : ''}>Notifications</span>}
          </button>
          <button 
            onClick={() => handleTabChange('messages')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left relative ${
              activeTab === 'messages' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Messages' : ''}
          >
            <div className="relative">
              <MessageSquare className="w-6 h-6 text-cyan-500" />
              {unreadDMs > 0 && (
                <span className="absolute -top-2 -right-2 bg-cyan-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {unreadDMs > 9 ? '9+' : unreadDMs}
                </span>
              )}
            </div>
            {!sidebarCollapsed && <span className={activeTab === 'messages' ? 'text-cyan-400' : ''}>Messages</span>}
          </button>
          <button
            onClick={() => {
              handleUserSelection(currentUser);
              handleTabChange('profile');
            }}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'profile' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Profile' : ''}
          >
            <User className="w-6 h-6 text-green-500" />
            {!sidebarCollapsed && <span className={activeTab === 'profile' ? 'text-green-400' : ''}>Profile</span>}
          </button>
          <button 
            onClick={() => handleTabChange('tokenomics')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'tokenomics' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Tokenomics' : ''}
          >
            <Coins className="w-6 h-6 text-purple-500" />
            {!sidebarCollapsed && <span className={activeTab === 'tokenomics' ? 'text-purple-400' : ''}>Tokenomics</span>}
          </button>
          <button 
            onClick={() => handleTabChange('terms')}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-3 rounded-full hover:bg-gray-900 transition-colors w-full text-left ${
              activeTab === 'terms' ? 'font-bold' : ''
            }`}
            title={sidebarCollapsed ? 'Terms' : ''}
          >
            <Shield className="w-6 h-6 text-gray-500" />
            {!sidebarCollapsed && <span className={activeTab === 'terms' ? 'text-gray-400' : ''}>Terms</span>}
          </button>
          
          {/* Action Buttons - Responsive Carousel */}
          <div className="pt-4 lg:pt-5 xl:pt-6 2xl:pt-8 mb-4 lg:mb-5 xl:mb-6 2xl:mb-8">
            {isSmallScreen && !sidebarCollapsed ? (
              // Carousel mode for small screens
              <div className="relative">
                {/* Navigation Arrows */}
                <button
                  onClick={prevButton}
                  className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-gray-800/80 rounded-full hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-300" />
                </button>
                <button
                  onClick={nextButton}
                  className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-gray-800/80 rounded-full hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </button>

                {/* Active Button */}
                <div className="px-4">
                  {(() => {
                    const button = actionButtons[activeButtonIndex];
                    const Icon = button.icon;
                    return (
                      <button
                        onClick={button.onClick}
                        className={`group relative w-full bg-gradient-to-r ${button.gradient} text-white font-bold py-3 rounded-full transition-all duration-500 transform hover:scale-105 hover:${button.shadowColor} overflow-hidden`}
                      >
                        <div className="relative z-10 flex items-center justify-center gap-2">
                          <Icon className="w-5 h-5" />
                          <span>{button.label}</span>
                          {button.hasNew && (
                            <span className="px-2 py-0.5 bg-yellow-400 text-black text-xs font-bold rounded-full animate-pulse">
                              NEW
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })()}
                </div>

                {/* Dots Indicator */}
                <div className="flex justify-center gap-1.5 mt-3">
                  {actionButtons.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveButtonIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === activeButtonIndex
                          ? 'bg-purple-500 w-6'
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    />
                  ))}
                </div>
              </div>
            ) : (
              // Regular mode for large screens or collapsed sidebar
              <div className="space-y-3 lg:space-y-4 xl:space-y-5 2xl:space-y-6">
                {actionButtons.map((button) => {
                  const Icon = button.icon;
                  return (
                    <button
                      key={button.id}
                      onClick={button.onClick}
                      className={`group relative w-full bg-gradient-to-r ${button.gradient} text-white font-bold py-3 rounded-full transition-all duration-500 transform hover:scale-110 hover:rotate-1 hover:shadow-2xl ${button.shadowColor} overflow-hidden ${
                        sidebarCollapsed ? 'px-0' : ''
                      }`}
                      title={sidebarCollapsed ? button.label : ''}
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute inset-0 bg-gradient-to-t from-white/10 via-white/5 to-transparent animate-pulse"></div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      
                      <div className="relative z-10 flex items-center justify-center gap-2">
                        <Icon className={`${sidebarCollapsed ? 'w-5 h-5 mx-auto' : 'w-5 h-5'} group-hover:animate-pulse transition-transform duration-300`} />
                        {!sidebarCollapsed && (
                          <>
                            <span className="group-hover:tracking-wide transition-all duration-300">{button.label}</span>
                            {button.hasNew && (
                              <span className="px-2 py-0.5 bg-yellow-400 text-black text-xs font-bold rounded-full animate-pulse">
                                NEW
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Collapse/Expand Toggle Button */}
          {!sidebarCollapsed && (
            <div className="relative mt-6">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="absolute -right-6 top-0 flex items-center justify-center bg-gradient-to-r from-pink-500/20 to-purple-600/20 border border-pink-500/30 rounded-lg p-1.5 hover:from-pink-500/30 hover:to-purple-600/30 transition-all hover:scale-105 shadow-lg group z-[9999]"
                title="Collapse sidebar"
              >
                <PanelLeftClose className="w-5 h-5 text-pink-400 group-hover:text-pink-300" />
              </button>
            </div>
          )}
          
          {/* Collapse button for when sidebar is collapsed */}
          {sidebarCollapsed && (
            <div className="relative mt-4">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="absolute -right-6 top-0 flex items-center justify-center bg-gradient-to-r from-pink-500/20 to-purple-600/20 border border-pink-500/30 rounded-lg p-1.5 hover:from-pink-500/30 hover:to-purple-600/30 transition-all hover:scale-105 shadow-lg group z-[9999]"
                title="Expand sidebar"
              >
                <PanelLeftOpen className="w-5 h-5 text-pink-400 group-hover:text-pink-300" />
              </button>
            </div>
          )}
        </nav>
        </div>
        
        {/* Current User Profile Section - Fixed at bottom */}
        <div className="p-4 pt-2 border-t border-gray-800">
          <div 
            className="hover:bg-gray-900/50 rounded-full p-2 transition-colors cursor-pointer"
            onClick={() => {
              handleUserSelection(currentUser);
              handleTabChange('profile');
            }}
            title={sidebarCollapsed ? currentUser?.username : ''}
          >
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
              <div className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'} flex-1 min-w-0`}>
                <img 
                  src={currentUser?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser?.username || 'default'}`} 
                  alt={currentUser?.username || 'User'} 
                  className="w-10 h-10 rounded-full"
                />
                {!sidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-[15px] truncate flex items-center">
                      {currentUser?.username || 'Loading...'}
                      <VerifiedBadge username={currentUser?.username} />
                    </p>
                    <p className="text-gray-500 text-[15px] truncate">{currentUser?.handle || '@loading'}</p>
                  </div>
                )}
              </div>
              {!sidebarCollapsed && (
                <div className="relative">
                <button 
                  data-dropdown-toggle="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Toggle dropdown menu
                    const dropdown = document.getElementById('profile-dropdown');
                    if (dropdown) {
                      dropdown.classList.toggle('hidden');
                    }
                  }}
                  className="p-1.5 text-gray-500 hover:text-white transition-colors"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
                <div 
                  id="profile-dropdown"
                  className="hidden absolute bottom-full right-0 mb-2 w-60 bg-black border border-gray-800 rounded-2xl shadow-xl py-1"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors flex items-center space-x-3 text-[15px]"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Log out {currentUser?.handle || ''}</span>
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Burn Modal */}
      <BurnForBoostModal 
        isOpen={showBurnModal}
        onClose={() => setShowBurnModal(false)}
      />
    </div>
  );
};

export default DesktopSidebar;